import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
/**
 * User Details Modal Component
 * Displays user profile information, site permissions, and action controls.
 */

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Check, X, Mail, Phone, Building2, UserCheck,
  Calendar, Power, Shield, MapPin,
  ShieldCheck, User as UserIcon, Ban, UserCog, KeyRound, Loader2,
} from 'lucide-react';
import { SetTempPasswordDialog } from './SetTempPasswordDialog';
import { PrimaryContactSection } from './PrimaryContactSection';
import { UserBuyerCodesField } from './UserBuyerCodesField';
import { UserProfile, UserSitePermission, coreAPI } from '../../lib/api';
import { toast } from 'sonner';
import { UserDetailsSiteAccessTab } from './UserDetailsSiteAccessTab';
import { canSendInvitation } from './userInvitation';
import { useFormatters } from '../../contexts/PreferencesContext';

function Label({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={className}>{children}</div>;
}

interface UserDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: UserProfile | null;
  userDetailTab: 'profile' | 'companies';
  setUserDetailTab: (tab: 'profile' | 'companies') => void;
  userSitePermissions: UserSitePermission[];
  loadingSitePermissions: boolean;
  savingPermission: number | null;
  savingDivision: number | null;
  /** True when the logged-in user is a Django superuser (may use Super admin switch). */
  isSuperAdmin: boolean;
  actionLoading: boolean;
  isSelf: (userProfile: UserProfile) => boolean;
  handleToggleActive: (userProfile: UserProfile) => void;
  openChangeRoleDialog: (userProfile: UserProfile) => void;
  /** Platform staff only: open the change-company (relink) dialog. */
  openChangeCompanyDialog?: (userProfile: UserProfile) => void;
  handleToggleAdmin: (userProfile: UserProfile, isAdmin: boolean) => void;
  handleToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  handleToggleSitePermission: (siteId: number, permission: string, value: boolean) => Promise<void>;
  handleAssignDivision: (
    groupId: number,
    opts: { isLinked: boolean; modules?: Record<string, boolean>; applyModulesToAll?: boolean }
  ) => Promise<void>;
  handleApproveUser: (userId: number) => Promise<void>;
  handleRejectUser: (userId: number) => Promise<void>;
  handleSendInvitation: (userProfile: UserProfile) => Promise<void>;
  /** CR-6273: true when the logged-in admin may impersonate (VC-internal or
   *  customer admin). Drives whether the "Login As" footer button is shown. */
  canImpersonate?: boolean;
  /** CR-6273: impersonate the given user (scoped to this User-management view). */
  handleLoginAs?: (userProfile: UserProfile) => void;
  /** SI-25: user id whose impersonation request is in flight. The footer button
   *  disables and spins on it, as the caller's row action already does — the
   *  server rotates the admin's own token per call, so a second click destroys
   *  the original_token the first one returned. */
  impersonatingUserId?: number | null;
  /** Refresh the user list after primary-contact designations change, so the
   *  row (and the Supplier Detail badge) reflect the new assignment. */
  onPrimaryContactsSaved?: () => void;
}

export function UserDetailsModal({
  open,
  onOpenChange,
  selectedUser,
  userDetailTab,
  setUserDetailTab,
  userSitePermissions,
  loadingSitePermissions,
  savingPermission,
  savingDivision,
  isSuperAdmin,
  actionLoading,
  isSelf,
  handleToggleActive,
  openChangeRoleDialog,
  openChangeCompanyDialog,
  handleToggleAdmin,
  handleToggleSiteLink,
  handleToggleSitePermission,
  handleAssignDivision,
  handleApproveUser,
  handleRejectUser,
  handleSendInvitation,
  canImpersonate = false,
  handleLoginAs,
  impersonatingUserId = null,
  onPrimaryContactsSaved,
}: UserDetailsModalProps) {
  const { formatDateTime } = useFormatters();

  // Temporary password. Reaching this modal already means the viewer passed
  // CanManageUsers, and the backend re-checks it per target (own company under
  // the admin-tier rule, or a supplier they trade with) and refuses staff
  // accounts — so customer admins get it too, for invitations that never
  // arrive and therefore can't be recovered by the reset email either.
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);

  // Platform-staff toggle. Superuser-only on both sides: the backend refuses
  // anyone else and serializes is_platform_staff as null for them, so a
  // customer admin never even learns the flag's state. Optimistic local copy
  // so the switch reflects the change without refetching the whole list.
  const { user: viewer } = useAuth();
  const viewerIsSuperuser = Boolean(viewer?.is_superuser);
  const [staffFlag, setStaffFlag] = useState<boolean | null>(null);
  const [savingStaffFlag, setSavingStaffFlag] = useState(false);
  useEffect(() => {
    setStaffFlag(selectedUser?.is_platform_staff ?? null);
  }, [selectedUser]);

  const togglePlatformStaff = async (next: boolean) => {
    if (!selectedUser) return;
    setSavingStaffFlag(true);
    try {
      const res = await coreAPI.setPlatformStaff(selectedUser.id, next);
      setStaffFlag(res.data.is_staff);
      toast.success(res.data.message);
    } catch (err: unknown) {
      console.error('Failed to change platform staff access:', err);
      const detail = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(detail || 'Failed to change platform staff access');
    } finally {
      setSavingStaffFlag(false);
    }
  };

  /**
   * Persists the chosen password.
   *
   * @returns true when it was accepted, so the dialog stays open on a rejected
   *   password and the value typed is not lost.
   */
  const setTempPasswordOnUser = async (password: string): Promise<boolean> => {
    if (!selectedUser || !password) return false;
    setSettingPassword(true);
    try {
      await coreAPI.setTempUserPassword(selectedUser.id, password);
      toast.success('Temporary password set — share it with the user securely');
      return true;
    } catch (err: unknown) {
      console.error('Failed to set temp password:', err);
      const detail = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(detail || 'Failed to set temporary password');
      return false;
    } finally {
      setSettingPassword(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(openVal) => {
      onOpenChange(openVal);
      if (!openVal) setUserDetailTab('profile');
    }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            User Details
          </DialogTitle>
          <DialogDescription>
            {selectedUser?.user.full_name} • {selectedUser?.user.email}
          </DialogDescription>
        </DialogHeader>

        {selectedUser && (
          <Tabs value={userDetailTab} onValueChange={(v) => setUserDetailTab(v as 'profile' | 'companies')} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="profile" className="gap-2">
                <UserIcon className="h-4 w-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="companies" className="gap-2">
                <Building2 className="h-4 w-4" />
                Related Companies
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="flex-1 overflow-y-auto mt-4 space-y-6">
              {/* Status & Actions Bar */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  {/* CR-4551 round 4: surface the "Invite Sent" intermediate
                      state instead of mis-labelling unaccepted invitees as
                      Active in the User Details modal. */}
                  {!selectedUser.user.is_active ? (
                    <Badge variant="secondary">
                      <X className="h-3 w-3 mr-1" />
                      Inactive
                    </Badge>
                  ) : selectedUser.user.invitation_sent && !selectedUser.user.has_joined ? (
                    <Badge variant="outline" className="text-amber-600 border-amber-600">
                      Invite Sent
                    </Badge>
                  ) : (
                    <Badge variant="default" className="bg-green-500">
                      <Check className="h-3 w-3 mr-1" />
                      Active
                    </Badge>
                  )}
                  {!selectedUser.is_approved && (
                    <Badge variant="outline" className="text-amber-600 border-amber-600">
                      Pending Approval
                    </Badge>
                  )}
                  {selectedUser.role_details?.is_admin_role && (
                    <Badge variant="outline" className="text-blue-600 border-blue-600">
                      <ShieldCheck className="h-3 w-3 mr-1" />
                      Admin role
                    </Badge>
                  )}
                  {selectedUser.user.is_superuser && (
                    <Badge variant="outline" className="text-purple-600 border-purple-600">
                      <ShieldCheck className="h-3 w-3 mr-1" />
                      Super admin
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  {!isSelf(selectedUser) && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleActive(selectedUser)}
                        disabled={actionLoading}
                      >
                        <Power className="h-4 w-4 mr-1" />
                        {selectedUser.user.is_active ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openChangeRoleDialog(selectedUser)}
                      >
                        <Shield className="h-4 w-4 mr-1" />
                        Change Role
                      </Button>
                      {openChangeCompanyDialog && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openChangeCompanyDialog(selectedUser)}
                          disabled={actionLoading}
                        >
                          <Building2 className="h-4 w-4 mr-1" />
                          Change Company
                        </Button>
                      )}
                      {/* CR-5367: Super Admin toggle is only ever applicable
                          to Valuechain (internal Helix) users — Customer and
                          Supplier user details must not show this control,
                          and the caller must already hold Super Admin rights
                          to grant/revoke. The two gates compose so that a
                          Valuechain super admin opening a Customer/Supplier
                          user detail will not see the toggle either. */}
                      {isSuperAdmin && selectedUser.company_type === 'helix' && (
                        <div className="flex items-center gap-2 ml-2 pl-2 border-l">
                          <label htmlFor="user-super-admin-toggle" className="text-sm text-muted-foreground">
                            Super admin
                          </label>
                          <Switch
                            id="user-super-admin-toggle"
                            checked={selectedUser.user.is_superuser || false}
                            onCheckedChange={(checked) => handleToggleAdmin(selectedUser, checked)}
                            disabled={actionLoading}
                            aria-label="Grant or revoke Django superuser and staff"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Full Name</Label>
                  <p className="text-base mt-1">{selectedUser.user.full_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {selectedUser.user.email}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Company</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {selectedUser.company_name}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">User Role</Label>
                  {/* div, not p: Badge renders a div, and a div inside a p is
                      invalid HTML that React reports as a hydration error. */}
                  <div className="text-base mt-1">
                    <Badge variant="outline">{selectedUser.role_name}</Badge>
                  </div>
                </div>
              </div>

              {/* Buyer codes (customer material controllers): drives the
                  "my suppliers" default scoping on purchasing list views. */}
              {selectedUser.company_type === 'customer' && (
                <UserBuyerCodesField selectedUser={selectedUser} />
              )}

              {/* Set a temporary password on this managed customer/supplier
                  user (no email round-trip). Helix-tenant targets stay excluded
                  here, and the backend refuses staff accounts outright. */}
              {selectedUser.company_type !== 'helix' && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Temporary Password</Label>
                  <div className="mt-1">
                    <Button size="sm" variant="outline" onClick={() => setPasswordDialogOpen(true)}>
                      <KeyRound className="h-4 w-4 mr-2" />
                      Set temporary password
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Applies immediately — share it with the user securely. They can change it in My Account.
                  </p>
                </div>
              )}

              {/* Platform staff. Super admins only — this is the operator
                  bypass, not a company admin marker, so no one else sees or
                  sets it. Offered on admin-role users (who are the ones that
                  need it) and on anyone already holding it, so it can be
                  reviewed and revoked from the same place. */}
              {viewerIsSuperuser
                && selectedUser.company_type !== 'helix'
                && (selectedUser.role_details?.is_admin_role || staffFlag) && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Platform Staff</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <Switch
                      checked={Boolean(staffFlag)}
                      disabled={savingStaffFlag}
                      onCheckedChange={togglePlatformStaff}
                      aria-label="Platform staff access"
                    />
                    <span className="text-sm">
                      {savingStaffFlag
                        ? 'Saving…'
                        : staffFlag ? 'Has platform staff access' : 'No platform staff access'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Grants operator-level access across tenants — platform settings, email queue
                    and temporary passwords. Give it only to people who run the platform.
                  </p>
                </div>
              )}

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4">
                {selectedUser.job_title && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Position</Label>
                    <p className="text-base mt-1">{selectedUser.job_title}</p>
                  </div>
                )}
                {selectedUser.department && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Department</Label>
                    <p className="text-base mt-1">{selectedUser.department}</p>
                  </div>
                )}
                {selectedUser.phone && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Telephone</Label>
                    <p className="text-base mt-1 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {selectedUser.phone}
                    </p>
                  </div>
                )}
              </div>

              {/* Sites Summary */}
              {selectedUser.site_names.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Current Company Access</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedUser.site_names.map((site, idx) => (
                      <Badge key={idx} variant="secondary" className="gap-1">
                        <MapPin className="h-3 w-3" />
                        {site}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Primary contact designations — supplier and/or customer
                  companies this user fronts. Helix companies are excluded
                  server-side, so hide the editor for internal accounts. */}
              {selectedUser.company_type !== 'helix' && (
                <div className="pt-4 border-t">
                  <PrimaryContactSection
                    selectedUser={selectedUser}
                    canManage={Boolean(selectedUser.can_manage_user)}
                    onSaved={onPrimaryContactsSaved}
                  />
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Last login</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {selectedUser.user.last_login
                      ? formatDateTime(selectedUser.user.last_login)
                      : 'Never'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Created At</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatDateTime(selectedUser.created_at)}
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="companies" className="flex-1 overflow-y-auto mt-4">
              <UserDetailsSiteAccessTab
                userSitePermissions={userSitePermissions}
                loadingSitePermissions={loadingSitePermissions}
                savingPermission={savingPermission}
                savingDivision={savingDivision}
                selectedUser={selectedUser}
                isSelf={isSelf}
                handleToggleSiteLink={handleToggleSiteLink}
                handleToggleSitePermission={handleToggleSitePermission}
                handleAssignDivision={handleAssignDivision}
              />
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter className="border-t pt-4 flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {/* CR-6273: Login As lives only here, in the user-detail view, so it
              is always scoped to the user the admin is inspecting. Hidden for
              self, inactive users, and Helix platform users (also blocked
              server-side — a Helix account must never be impersonated). */}
          {canImpersonate && handleLoginAs && selectedUser
            && !isSelf(selectedUser)
            && selectedUser.company_type !== 'helix'
            && selectedUser.user.is_active && (
            <Button
              variant="outline"
              disabled={actionLoading || impersonatingUserId === selectedUser.user.id}
              onClick={() => handleLoginAs(selectedUser)}
            >
              {impersonatingUserId === selectedUser.user.id ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserCog className="h-4 w-4 mr-2" />
              )}
              Login As
            </Button>
          )}
          {/* CR-5314: hide the invitation button for any user shown as
              "Active" (joined, or active and never invited). It stays visible
              only for non-active users — "Invite Sent" (resend) and inactive
              not-yet-joined accounts. See canSendInvitation for the rule. */}
          {selectedUser && !isSelf(selectedUser) && canSendInvitation(selectedUser.user) && (
            <Button
              variant="outline"
              disabled={actionLoading}
              onClick={async () => {
                await handleSendInvitation(selectedUser);
              }}
            >
              <Mail className="h-4 w-4 mr-2" />
              Send invitation email
            </Button>
          )}
          {selectedUser && !selectedUser.is_approved && !isSelf(selectedUser) && (
            <>
              <Button
                variant="destructive"
                disabled={actionLoading}
                onClick={async () => {
                  await handleRejectUser(selectedUser.id);
                }}
              >
                <Ban className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button
                disabled={actionLoading}
                onClick={async () => {
                  await handleApproveUser(selectedUser.id);
                  onOpenChange(false);
                }}
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Approve user
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Sibling of the details dialog, not a child: nesting it inside the
        Radix root left it unable to open. */}
    {selectedUser && (
      <SetTempPasswordDialog
        open={passwordDialogOpen}
        onOpenChange={setPasswordDialogOpen}
        userName={selectedUser.user.full_name || selectedUser.user.email}
        saving={settingPassword}
        onSubmit={setTempPasswordOnUser}
      />
    )}
    </>
  );
}
